import { formatDateRange } from '../formatDate';
import { parseAmount } from '../parseAmount';
/**
 * Utilidades compartidas de las pantallas de cotización.
 */

export const STATUS_COLORS = {
  draft: 'default',
  quoted: 'info',
  in_use: 'success',
  archived: 'default',
};

export const formatPeriod = (period) => {
  if (!period?.startDate) return '—';
  return `${formatDateRange(period.startDate, period.endDate)} (${period.days ?? '?'}d)`;
};

// Período de cotización: de la fecha de ingreso al término, que por defecto
// es fin del mes de ingreso. Desde el 2026-10-31 puede abarcar varios meses,
// sin tope (cotizaciones por más de un período); entre el 2026-10-13 y esa
// fecha era de máximo un mes (minuta OUT/EST).
// Último día del mes de una fecha ISO (YYYY-MM-DD) → ISO
export const endOfMonthIso = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [year, month] = iso.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  return `${iso.slice(0, 7)}-${String(lastDay).padStart(2, '0')}`;
};

// Término >= inicio, en cualquier mes (misma regla que el backend)
export const isValidQuotationPeriod = (startDate, endDate) =>
  Boolean(startDate && endDate) && endDate >= startDate;

// Al elegir el inicio (misma regla que el backend al correr el ingreso,
// ErpQuotation.periodWithNewEntry): un período de varios meses conserva su
// término si no queda antes del nuevo inicio; uno de un mes sigue de un mes
// (conserva el término solo si cae en el mes del nuevo inicio). Si no, o si no
// había término, fin del mes del inicio. `previousStart`: el inicio anterior.
export const adjustEndDate = (startDate, endDate, previousStart = null) => {
  if (!startDate) return endDate;
  if (!isValidQuotationPeriod(startDate, endDate)) return endOfMonthIso(startDate);
  const spannedMonths = Boolean(previousStart) && previousStart.slice(0, 7) !== endDate.slice(0, 7);
  return spannedMonths || endDate.slice(0, 7) === startDate.slice(0, 7) ? endDate : endOfMonthIso(startDate);
};

export const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return `$${Number(value).toLocaleString('es-CL')}`;
};

// Estructura de renta vacía (shape de erpQuotations.salaryStructure)
export const emptySalaryStructure = {
  baseSalary: '',
  taxableBonuses: [],
  mealAllowance: '',
  transportAllowance: '',
  teleworkAllowance: '',
  otherAllowances: [],
  additionalProvisions: [],
  gratificationType: 'legal_monthly',
  vacationProvision: true, // planilla remuneraciones B49 "PROVISIONES Si(1)/No(0)"
  // Monto MENSUAL pactado de la provisión de vacaciones (fase 1b, 2026-10-13):
  // vacío = automático (total imponible / 30 × 1,75)
  vacationProvisionAmount: '',
  targetNetSalary: '',
};

// Montos escritos a mano: formato chileno ("600.000" = 600000, "41.057,2" = 41057.2)
const toNumberOrNull = (v) => parseAmount(v);

// Monto guardado (número) → texto del campo, que parseAmount vuelve a leer
// igual: decimal con coma ("1,234" = 1,234 y no 1.234 mil). Edición, 2026-10-13.
export const amountToInput = (value) => {
  if (value === null || value === undefined || value === '') return '';
  return typeof value === 'number' ? String(value).replace('.', ',') : String(value);
};

// salaryStructure guardada → estado del formulario del wizard (modo edición)
export const salaryStructureToForm = (s) => {
  const src = s || {};
  const rows = (list) => (list || []).map((r) => ({ name: r.name || '', amount: amountToInput(r.amount) }));
  return {
    ...emptySalaryStructure,
    baseSalary: amountToInput(src.baseSalary),
    taxableBonuses: rows(src.taxableBonuses),
    mealAllowance: amountToInput(src.mealAllowance),
    transportAllowance: amountToInput(src.transportAllowance),
    teleworkAllowance: amountToInput(src.teleworkAllowance),
    otherAllowances: rows(src.otherAllowances),
    additionalProvisions: rows(src.additionalProvisions),
    gratificationType: src.gratificationType || 'legal_monthly',
    vacationProvision: src.vacationProvision !== false,
    vacationProvisionAmount: amountToInput(src.vacationProvisionAmount),
    targetNetSalary: amountToInput(src.targetNetSalary),
  };
};

/**
 * Ingreso mínimo (fase 1b del cotizador OUT/EST, 2026-10-13): el backend
 * responde 422 BELOW_MINIMUM_WAGE con { monthlyBaseSalary, minimumWage,
 * parameterSetPeriod, solvedFromNet, targetNetSalary } cuando el sueldo base
 * mensual (el digitado o el que resulta desde el líquido) queda bajo el IMM
 * de los parámetros del mes de ingreso. Texto del motivo, sin la acción.
 */
export const MINIMUM_WAGE_CODE = 'BELOW_MINIMUM_WAGE';
export const minimumWageMessage = (t, data) => {
  const params = {
    base: formatMoney(data?.monthlyBaseSalary),
    minimum: formatMoney(data?.minimumWage),
    period: data?.parameterSetPeriod || '—',
    target: formatMoney(data?.targetNetSalary),
  };
  return data?.solvedFromNet
    ? t('erp.quotations.minimumWage.fromNet', params)
    : t('erp.quotations.minimumWage.base', params);
};

// Estados sin contrato: la cotización se edita (fase 2 del cotizador OUT/EST)
export const EDITABLE_STATUSES = ['draft', 'quoted'];

// Personas con contrato en una cotización (nombres; KAM no los ve)
export const contractPeople = (contracts) =>
  (contracts || []).map((c) => c.workerName).filter(Boolean).join(', ');

/**
 * Motivo por el que una cotización no se puede editar (decisión Erwin
 * 2026-10-13: el botón queda deshabilitado con un tooltip que lo explica).
 * contracts: los de la cotización (endpoint family), para nombrar a la
 * persona. Devuelve { key, person } (clave de erp.quotations.editBlocked) o null.
 */
export const quotationEditBlock = (quotation, contracts) => {
  if (!quotation) return null;
  const list = contracts || [];
  if (quotation.status === 'in_use' || list.length > 0) {
    const person = contractPeople(list);
    return { key: person ? 'contract' : 'contractNoName', person };
  }
  if (quotation.status === 'archived') return { key: 'archived', person: '' };
  return null;
};

/**
 * Condiciones comerciales editables en la cotización (fase 4 del cotizador
 * OUT/EST, 2026-10-13). Se heredan del acuerdo (el del centro de costo o, si
 * no hay, el de todo el cliente) y se pueden cambiar; el backend marca en
 * `overrides` lo que difiere de lo heredado (`inherited`).
 */
export const FORMULA_PROVISIONS = ['notice', 'severance', 'vacationReplacement'];
export const EDITABLE_CONDITIONS = ['margin', 'marginMinimum', 'operationalExpenses', 'adminExpense', 'assistanceCost', 'provisions'];

const toggleToForm = (v) => ({ enabled: Boolean(v?.enabled), amount: v?.enabled ? amountToInput(v.amount) : '' });

// Cotización anterior a la fase 4 (snapshot sin gastos operacionales, o la
// marca `fromParameters` en lo heredado): el motor usa los del set de
// parámetros. En el formulario quedan encendidos y sin monto hasta que se toquen.
const fromParameters = (snapshot, v) => Boolean(snapshot) && (v === undefined || v?.fromParameters === true);

// Snapshot (heredado o guardado) → formulario del wizard
export const conditionsToForm = (snapshot) => {
  const s = snapshot || {};
  const formula = s.provisions?.formula || {};
  return {
    marginFeeType: s.margin?.feeType || 'percentage',
    marginValue: amountToInput(s.margin?.value),
    marginMinimum: amountToInput(s.marginMinimum),
    operationalExpenses: fromParameters(snapshot, s.operationalExpenses)
      ? { enabled: true, amount: '', fromParameters: true }
      : toggleToForm(s.operationalExpenses),
    adminExpense: toggleToForm(s.adminExpense),
    assistanceCost: toggleToForm(s.assistanceCost),
    usesPurchaseOrder: Boolean(s.usesPurchaseOrder),
    provisions: Object.fromEntries(FORMULA_PROVISIONS.map((key) => [key, formula[key] === true])),
    fixedProvisions: (s.provisions?.fixed || []).map((p) => ({
      name: p.name || '',
      amount: amountToInput(p.amount),
      prorated: p.prorated === true,
    })),
  };
};

const toggleFromForm = (v) => (v?.fromParameters
  ? { fromParameters: true }
  : { enabled: Boolean(v?.enabled), amount: v?.enabled ? toNumberOrNull(v.amount) : null });

// Nombre de una provisión fija tal como lo guarda el backend (sin espacios de más)
const provisionName = (name) => String(name || '').trim();

// Formulario → condiciones efectivas (mismo shape que el snapshot del backend)
export const conditionsFromForm = (form) => ({
  margin: form.marginValue !== '' && form.marginValue !== null && form.marginValue !== undefined
    ? { feeType: form.marginFeeType || 'percentage', value: toNumberOrNull(form.marginValue) }
    : null,
  marginMinimum: form.marginMinimum !== '' && form.marginMinimum !== null && form.marginMinimum !== undefined
    ? toNumberOrNull(form.marginMinimum)
    : null,
  operationalExpenses: toggleFromForm(form.operationalExpenses),
  adminExpense: toggleFromForm(form.adminExpense),
  assistanceCost: toggleFromForm(form.assistanceCost),
  usesPurchaseOrder: Boolean(form.usesPurchaseOrder),
  provisions: {
    formula: Object.fromEntries(FORMULA_PROVISIONS.map((key) => [key, form.provisions?.[key] === true])),
    fixed: (form.fixedProvisions || [])
      .filter((p) => provisionName(p.name) || toNumberOrNull(p.amount) !== null)
      .map((p) => ({ name: provisionName(p.name), amount: toNumberOrNull(p.amount), prorated: p.prorated === true })),
  },
});

// Monto escrito que no se entiende como número ("12%", "5 mil"): no se deja
// avanzar ni guardar (si no, viajaría vacío y el margen o el gasto quedaría en 0)
export const isInvalidAmount = (value) =>
  value !== '' && value !== null && value !== undefined && toNumberOrNull(value) === null;

// Monto sin escribir. Con el interruptor encendido (o en una provisión fija con
// nombre) el gasto quedaría en $0 sin avisar: tampoco se deja avanzar ni
// guardar. Ajuste 2026-10-14.
export const isEmptyAmount = (value) =>
  value === null || value === undefined || String(value).trim() === '';

// Campos del formulario de condiciones con montos que no se entienden o que
// faltan. Los gastos operacionales "el de Parámetros" (cotización anterior que
// no los ha tocado) van sin monto a propósito.
export const invalidConditionFields = (form) => {
  const fields = [];
  if (isInvalidAmount(form.marginValue)) fields.push('marginValue');
  if (isInvalidAmount(form.marginMinimum)) fields.push('marginMinimum');
  ['operationalExpenses', 'adminExpense', 'assistanceCost'].forEach((key) => {
    const toggle = form[key];
    if (!toggle?.enabled) return;
    if (isInvalidAmount(toggle.amount) || (!toggle.fromParameters && isEmptyAmount(toggle.amount))) fields.push(key);
  });
  (form.fixedProvisions || []).forEach((p, index) => {
    if (isInvalidAmount(p.amount) || (provisionName(p.name) && isEmptyAmount(p.amount))) {
      fields.push(`fixedProvisions.${index}`);
    }
  });
  return fields;
};

// Forma comparable de una condición (mismo criterio que el backend)
const comparableCondition = (key, value) => {
  if (key === 'margin') {
    return value && value.value !== null && value.value !== undefined
      ? { feeType: value.feeType === 'fixed' ? 'fixed' : 'percentage', value: Number(value.value) }
      : null;
  }
  if (key === 'marginMinimum') return value === null || value === undefined || value === '' ? null : Number(value);
  if (key === 'provisions') {
    const formula = value?.formula || {};
    return {
      formula: FORMULA_PROVISIONS.map((k) => formula[k] === true),
      fixed: (value?.fixed || []).map((p) => [provisionName(p.name), p.amount === null || p.amount === undefined ? null : Number(p.amount), p.prorated === true]),
    };
  }
  // gastos operacionales sin el campo (cotización anterior) o con la marca: los del set
  if (key === 'operationalExpenses' && (value === undefined || value?.fromParameters)) return 'parameters';
  return value && value.enabled ? { enabled: true, amount: value.amount === null || value.amount === undefined ? null : Number(value.amount) } : { enabled: false };
};

// Condiciones que difieren de lo heredado (para marcarlas en el wizard)
export const changedConditions = (effective, inherited) =>
  EDITABLE_CONDITIONS.filter(
    (key) => JSON.stringify(comparableCondition(key, effective?.[key])) !== JSON.stringify(comparableCondition(key, inherited?.[key]))
  );

export const salaryStructureToPayload = (s) => ({
  baseSalary: toNumberOrNull(s.baseSalary),
  taxableBonuses: (s.taxableBonuses || [])
    .filter((b) => b.name || b.amount)
    .map((b) => ({ name: b.name, amount: toNumberOrNull(b.amount) })),
  mealAllowance: toNumberOrNull(s.mealAllowance),
  transportAllowance: toNumberOrNull(s.transportAllowance),
  teleworkAllowance: toNumberOrNull(s.teleworkAllowance),
  otherAllowances: (s.otherAllowances || [])
    .filter((b) => b.name || b.amount)
    .map((b) => ({ name: b.name, amount: toNumberOrNull(b.amount) })),
  additionalProvisions: (s.additionalProvisions || [])
    .filter((b) => b.name || b.amount)
    .map((b) => ({ name: b.name, amount: toNumberOrNull(b.amount) })),
  gratificationType: s.gratificationType || null,
  vacationProvision: s.vacationProvision !== false,
  // con la provisión apagada no viaja monto (el motor lo ignoraría igual)
  vacationProvisionAmount: s.vacationProvision !== false ? toNumberOrNull(s.vacationProvisionAmount) : null,
  targetNetSalary: toNumberOrNull(s.targetNetSalary),
});
